import React, { useEffect, useState } from "react";
import { Grid, Skeleton } from "@mui/material";
import { Box } from "@mui/system";
import Chart from "react-apexcharts";
import { getDealerVisitPerDayADMIN, getPJPdataADMIN, getTimeSpentDealerADMIN, getVisitsByTimeSpent, getVisitsData } from "_controllers/officer";
import { PageHeader, SectionCard, EmptyState, useChartBase, mergeChartOptions } from "_shared/ui";
import ChartPeriodFilter from "./ChartPeriodFilter";
import { defaultChartPeriods, getDateRangeByPeriod } from "./chartPeriodUtils";

// Returns true only if at least one value is a finite number greater than 0.
// Handles donut series (array of numbers) and bar series (flatten data arrays before passing).
const hasData = (values) =>
    Array.isArray(values) &&
    values.some((v) => v != null && Number.isFinite(Number(v)) && Number(v) > 0);

// Fixed-height wrapper so every chart / skeleton / empty slot lines up on the grid.
const ChartSlot = ({ children }) => (
    <Box sx={{ height: 340, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {children}
    </Box>
);

const AdminDashboard = () => {
    const base = useChartBase();

    const [visitByDistance, setVisitByDistance] = useState();
    const [visitType, setVisitType] = useState();
    const [uniqueVisits, setUniqueVisits] = useState();
    const [activeVisits, setActiveVisits] = useState();
    const [activeDealerVisits, setActiveDealerVisits] = useState(0)
    const [inactiveDealerVisits, setInactiveDealerVisits] = useState(0)
    const [pjpData, setPjpData] = useState([])
    const [perDayVisitData, setPerDayVisitData] = useState([])
    const [dealerTimeSpent, setDealerTimeSpent] = useState({})

    const [loader1, setLoader1] = useState(true)          // Visit Type
    const [loaderDealerStatus, setLoaderDealerStatus] = useState(true)
    const [loaderUnique, setLoaderUnique] = useState(true) // Unique + Total/Active Base
    const [loader2, setLoader2] = useState(true)
    const [loader3, setLoader3] = useState(true)
    const [loader4, setLoader4] = useState(true)
    const [loader5, setLoader5] = useState(true)

    // Per-chart date-period filters (default: today). Visit Type, Dealer Status,
    // and Unique Dealer Visits each have their own independent range now, so each
    // fetches getVisitsData separately.
    const [chartPeriods, setChartPeriods] = useState(defaultChartPeriods)
    const handlePeriodChange = (chartName, period) =>
        setChartPeriods((prev) => ({ ...prev, [chartName]: period }))

    // Visit Type
    useEffect(() => {
        const fetchData = async () => {
            setLoader1(true)
            const { start, end } = getDateRangeByPeriod(chartPeriods.visitType)
            const data = await getVisitsData({ start, end });
            setVisitType(data?.visitType);
            setLoader1(false)
        }
        fetchData()
    }, [chartPeriods.visitType]);

    // Visit By Dealer Status
    useEffect(() => {
        const fetchData = async () => {
            setLoaderDealerStatus(true)
            const { start, end } = getDateRangeByPeriod(chartPeriods.dealerStatus)
            const data = await getVisitsData({ start, end });
            setActiveDealerVisits(data?.visitByDealerStatus?.reduce((acc, obj) => {
                if (obj._id === "A") {
                    acc += obj.count;
                }
                return acc;
            }, 0))
            setInactiveDealerVisits(data?.visitByDealerStatus?.reduce((acc, obj) => {
                if (obj._id === "I") {
                    acc += obj.count;
                }
                return acc;
            }, 0))
            setLoaderDealerStatus(false)
        }
        fetchData()
    }, [chartPeriods.dealerStatus]);

    // Unique Dealer Visits (also drives Total Visits / Active Dealer Base)
    useEffect(() => {
        const fetchData = async () => {
            setLoaderUnique(true)
            const { start, end } = getDateRangeByPeriod(chartPeriods.uniqueVisits)
            const data = await getVisitsData({ start, end });
            setUniqueVisits(data?.uniqueDealerVisits);
            setActiveVisits(data?.activeDealerVisits);
            setLoaderUnique(false)
        }
        fetchData()
    }, [chartPeriods.uniqueVisits]);

    useEffect(() => {
        const fetchData = async () => {
            setLoader2(true)
            const { start, end } = getDateRangeByPeriod(chartPeriods.location)
            const data = await getVisitsByTimeSpent({ start, end });
            setVisitByDistance(data);
            setLoader2(false)
        }
        fetchData()
    }, [chartPeriods.location]);

    useEffect(async () => {
        await getPJPdataADMIN({
            // startDate,
            // endDate,
        }).then((data) => {
            console.log("PJP raw response:", data) // check PJPCreated (YES) values per zone
            setPjpData(data)
            setLoader3(false)
        }).catch((err) => {
            console.log("err", err);
        })
    }, []);

    useEffect(async () => {
        await getDealerVisitPerDayADMIN({
            // startDate,
            // endDate,
        }).then((data) => {
            console.log("Dealer visits/day raw response:", data) // check currentMonth values per zone
            setPerDayVisitData(data)
            setLoader4(false)
        }).catch((err) => console.log("err", err))
    }, []);

    useEffect(async () => {
        await getTimeSpentDealerADMIN({
            // startDate,
            // endDate,
        }).then((data) => {
            let obj = {}
            obj["NZ"] = data?.data?.dataNZ.length > 0 ? data?.data?.dataNZ[0].time : 0,
                obj["SZ"] = data?.data?.dataSZ.length > 0 ? data?.data?.dataSZ[0].time : 0,
                obj["EZ"] = data?.data?.dataEZ.length > 0 ? data?.data?.dataEZ[0].time : 0,
                obj["WZ"] = data?.data?.dataWZ.length > 0 ? data?.data?.dataWZ[0].time : 0,
                obj["SZII"] = data?.data?.dataSZII.length > 0 ? data?.data?.dataSZII[0].time : 0,
                setDealerTimeSpent(obj)
            setLoader5(false)
            // setAvgTimeLoader(false)
        }).catch((err) => console.log("err", err))
    }, []);

    // useEffect(async () => {
    //     let val = navigator.geolocation.getCurrentPosition((res => console.log("VALUE", res)))
    //     console.log("VALUE", val);
    // }, [])

    console.log("loader", loader1, loader2, loader3, loader4, loader5);

    return (
        <>
            <PageHeader title="Admin Dashboard" subtitle="Field visit analytics overview" />
            <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                    <SectionCard
                        title="Visit Type"
                        action={
                            <ChartPeriodFilter
                                value={chartPeriods.visitType}
                                onChange={(p) => handlePeriodChange("visitType", p)}
                            />
                        }
                    >
                        <ChartSlot>
                            {loader1 ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([visitType?.planned, visitType?.unplanned]) ? (
                                <Chart
                                    type="donut"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[visitType.planned, visitType.unplanned]}
                                    options={mergeChartOptions(base, {
                                        labels: ["Planned", "Unplanned"],
                                        plotOptions: {
                                            pie: {
                                                startAngle: -90,
                                                endAngle: 90,
                                            },
                                        },
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                ></Chart>
                            ) : (
                                <EmptyState title="Visit Type" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard
                        title="Visit By Dealer Status"
                        action={
                            <ChartPeriodFilter
                                value={chartPeriods.dealerStatus}
                                onChange={(p) => handlePeriodChange("dealerStatus", p)}
                            />
                        }
                    >
                        <ChartSlot>
                            {loaderDealerStatus ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([activeDealerVisits, inactiveDealerVisits]) ? (
                                <Chart
                                    type="donut"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[activeDealerVisits ?? 0, inactiveDealerVisits ?? 0]}
                                    options={mergeChartOptions(base, {
                                        labels: ["Active", "In-Active"],
                                        plotOptions: {
                                            pie: {
                                                startAngle: -90,
                                                endAngle: 90,
                                            },
                                        },
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Visit By Dealer Status" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard
                        title="Unique Dealer Visits"
                        action={
                            <ChartPeriodFilter
                                value={chartPeriods.uniqueVisits}
                                onChange={(p) => handlePeriodChange("uniqueVisits", p)}
                            />
                        }
                    >
                        <ChartSlot>
                            {loaderUnique ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([uniqueVisits?.uniqueVisits, uniqueVisits?.totalVisits]) ? (
                                <Chart
                                    type="donut"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[
                                        uniqueVisits?.uniqueVisits,
                                        uniqueVisits?.totalVisits - uniqueVisits?.uniqueVisits,
                                    ]}
                                    options={mergeChartOptions(base, {
                                        labels: ["Unique Dealers Visits", "Repeated Dealers"],
                                        plotOptions: {
                                            pie: {
                                                startAngle: -90,
                                                endAngle: 90,
                                                donut: {
                                                    labels: {
                                                        show: true,
                                                        name: {
                                                            formatter: function (val) {
                                                                return "Total Visits";
                                                            },
                                                        },
                                                        total: {
                                                            show: true,
                                                            formatter: function (val) {
                                                                return uniqueVisits?.totalVisits;
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Unique Dealer Visits" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard title="Total Visits / Active Dealer Base">
                        <ChartSlot>
                            {loaderUnique ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([activeVisits?.visits, activeVisits?.totalDealers]) ? (
                                <Chart
                                    type="donut"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[activeVisits?.visits, activeVisits?.totalDealers]}
                                    options={mergeChartOptions(base, {
                                        labels: ["Unique Dealers", "Active Dealers"],
                                        plotOptions: {
                                            pie: {
                                                startAngle: -90,
                                                endAngle: 90,
                                            },
                                        },
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Total Visits / Active Dealer Base" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard
                        title="Visit By Start & End Location"
                        action={
                            <ChartPeriodFilter
                                value={chartPeriods.location}
                                onChange={(p) => handlePeriodChange("location", p)}
                            />
                        }
                    >
                        <ChartSlot>
                            {loader2 ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([visitByDistance?.pendingVisits, visitByDistance?.lessThan100, visitByDistance?.moreThan100]) ? (
                                <Chart
                                    type="donut"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[
                                        visitByDistance?.pendingVisits,
                                        visitByDistance?.lessThan100,
                                        visitByDistance?.moreThan100,
                                    ]}
                                    options={mergeChartOptions(base, {
                                        labels: ["Not-Ended", ">100m", "<100m"],
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Visit By Start & End Location" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard title="Average Dealer Visits / Day">
                        <ChartSlot>
                            {loader4 ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([
                                perDayVisitData?.northZoneData?.previousMonth,
                                perDayVisitData?.westZoneData?.previousMonth,
                                perDayVisitData?.eastZoneData?.previousMonth,
                                perDayVisitData?.southZoneData?.previousMonth,
                                perDayVisitData?.southZoneIIData?.previousMonth,
                                perDayVisitData?.northZoneData?.currentMonth,
                                perDayVisitData?.westZoneData?.currentMonth,
                                perDayVisitData?.eastZoneData?.currentMonth,
                                perDayVisitData?.southZoneData?.currentMonth,
                                perDayVisitData?.southZoneIIData?.currentMonth,
                            ]) ? (
                                <Chart
                                    type="bar"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[
                                        {
                                            name: "Last Month",
                                            data: [
                                                perDayVisitData?.northZoneData?.previousMonth ?? 0,
                                                perDayVisitData?.westZoneData?.previousMonth ?? 0,
                                                perDayVisitData?.eastZoneData?.previousMonth ?? 0,
                                                perDayVisitData?.southZoneData?.previousMonth ?? 0,
                                                perDayVisitData?.southZoneIIData?.previousMonth ?? 0,
                                            ],
                                        },
                                        {
                                            name: "Current Month",
                                            data: [
                                                perDayVisitData?.northZoneData?.currentMonth ?? 0,
                                                perDayVisitData?.westZoneData?.currentMonth ?? 0,
                                                perDayVisitData?.eastZoneData?.currentMonth ?? 0,
                                                perDayVisitData?.southZoneData?.currentMonth ?? 0,
                                                perDayVisitData?.southZoneIIData?.currentMonth ?? 0,
                                            ],
                                        },
                                    ]}
                                    options={mergeChartOptions(base, {
                                        labels: ["NORTH", "WEST", "EAST", "SOUTH-I", "SOUTH-II"],
                                        plotOptions: {
                                            bar: {
                                                columnWidth: '60%',
                                                dataLabels: {
                                                    position: 'top', // top, center, bottom
                                                },
                                            }
                                        },
                                        dataLabels: {
                                            enabled: true,
                                            offsetY: -20,
                                            // show every value, including 0, so a zero (e.g. current month) is still visible
                                            formatter: (val) => (val === null || val === undefined ? '' : val),
                                            style: {
                                                fontSize: '12px',
                                            }
                                        },
                                        stroke: {
                                            show: true,
                                            width: 3,
                                        },
                                        legend: {
                                            position: "top",
                                        },
                                        tooltip: { followCursor: true },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Average Dealer Visits / Day" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard title="PJP Planning Status">
                        <ChartSlot>
                            {loader3 ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([
                                pjpData?.EZData?.PJPNotCreated,
                                pjpData?.WZData?.PJPNotCreated,
                                pjpData?.SZData?.PJPNotCreated,
                                pjpData?.NZData?.PJPNotCreated,
                                pjpData?.SZIIData?.PJPNotCreated,
                                pjpData?.NZData?.PJPCreated,
                                pjpData?.WZData?.PJPCreated,
                                pjpData?.EZData?.PJPCreated,
                                pjpData?.SZData?.PJPCreated,
                                pjpData?.SZIIData?.PJPCreated,
                            ]) ? (
                                <Chart
                                    type="bar"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[
                                        {
                                            name: "NO",
                                            data: [
                                                pjpData?.NZData?.PJPNotCreated ?? 0,
                                                pjpData?.WZData?.PJPNotCreated ?? 0,
                                                pjpData?.EZData?.PJPNotCreated ?? 0,
                                                pjpData?.SZData?.PJPNotCreated ?? 0,
                                                pjpData?.SZIIData?.PJPNotCreated ?? 0
                                            ],
                                        },
                                        {
                                            name: "YES",
                                            data: [
                                                pjpData?.NZData?.PJPCreated ?? 0,
                                                pjpData?.WZData?.PJPCreated ?? 0,
                                                pjpData?.EZData?.PJPCreated ?? 0,
                                                pjpData?.SZData?.PJPCreated ?? 0,
                                                pjpData?.SZIIData?.PJPCreated ?? 0
                                            ],
                                        },
                                    ]}
                                    options={mergeChartOptions(base, {
                                        labels: ["NORTH", "WEST", "EAST", "SOUTH-I", "SOUTH-II"],
                                        plotOptions: {
                                            bar: {
                                                columnWidth: '60%',
                                                dataLabels: {
                                                    position: 'top',
                                                },
                                            }
                                        },
                                        dataLabels: {
                                            enabled: true,
                                            offsetY: -20,
                                            // show every value, including 0, so a zero (e.g. YES = 0) is still visible
                                            formatter: (val) => (val === null || val === undefined ? '' : val),
                                            style: {
                                                fontSize: '12px',
                                            }
                                        },
                                        stroke: {
                                            show: true,
                                            width: 3,
                                        },
                                        tooltip: { followCursor: true },
                                        legend: {
                                            position: "top",
                                        },
                                    })}
                                />
                            ) : (
                                <EmptyState title="PJP Planning Status" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard title="Average Time Spent / Dealer (mins)">
                        <ChartSlot>
                            {loader5 ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" />
                            ) : hasData([
                                dealerTimeSpent?.NZ,
                                dealerTimeSpent?.WZ,
                                dealerTimeSpent?.EZ,
                                dealerTimeSpent?.SZ,
                                dealerTimeSpent?.SZII,
                            ]) ? (
                                <Chart
                                    type="bar"
                                    width={'100%'}
                                    height={'100%'}
                                    series={[
                                        {
                                            name: "Last Month",
                                            data: [
                                                dealerTimeSpent?.NZ,
                                                dealerTimeSpent?.WZ,
                                                dealerTimeSpent?.EZ,
                                                dealerTimeSpent?.SZ,
                                                dealerTimeSpent?.SZII
                                            ],
                                        },
                                    ]}
                                    options={mergeChartOptions(base, {
                                        labels: ["NORTH", "WEST", "EAST", "SOUTH-I", "SOUTH-II"],
                                        plotOptions: {
                                            bar: {
                                                columnWidth: "70%",
                                                dataLabels: {
                                                    position: 'top',
                                                },
                                            }
                                        },
                                        dataLabels: {
                                            enabled: true,
                                            offsetY: -20,
                                            // hide "0" labels so zero-height bars don't leave floating labels
                                            formatter: (val) => (val ? val : ''),
                                            style: {
                                                fontSize: '12px',
                                            }
                                        },
                                        tooltip: { followCursor: true },
                                    })}
                                />
                            ) : (
                                <EmptyState title="Average Time Spent / Dealer (mins)" description="No data available" />
                            )}
                        </ChartSlot>
                    </SectionCard>
                </Grid>

            </Grid>
        </>
    )
}

export default AdminDashboard
